import React, { useContext, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import DealContext from '../../../../providers/Deal/Deal.context';
import { Button } from 'ncoded-component-library';
import PencilIcon from 'icons/Pencil.icon';
import PercentageIcon from 'icons/Percentage.icon';
import api from 'api';
import useCallbackRef from 'hooks/useCallbackRef';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import TransferArrowsIcon from 'icons/TransferArrows.icon';
import { DealEditDrawer } from '../../../../components/DealEditDrawer';
import { DropzoneDelete, DropzoneUpload } from 'models/Dropzone';
import Dropzone from 'components/Dropzone';
import { useNavigate } from 'react-router-dom';
import ThreeDotsDropdown from 'components/ThreeDotsDropdown';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import usePremiumFeatures from 'hooks/usePremiumFeatures';
import LockclosedIcon from 'icons/Lockclosed.icon';
import LockopenIcon from 'icons/Lockopen.icon';
import TagIcon from 'icons/Tag.icon';
import TransferDealToEnterpriseColleagueModal from '../TransferDealToEnterpriseColleagueModal';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import ListingContext from '../../../../providers/Listing/Listing.context';
import { getCreateDealRoute } from 'utils';

import './DealDetailsFormCard.styles.scss';
import './DealDetailsFormCard.styles.responsive.scss';

type DealDetailsFormCardProps = {
  className?: string;
  isDisabled?: boolean;
};

const DealDetailsFormCard: React.FC<DealDetailsFormCardProps> = (props) => {
  const { className, isDisabled } = props;

  const { t } = useTranslation();
  const {
    dealRoom,
    dealRoom: {
      _id: dealRoomId,
      name,
      industry,
      dealOutcome,
      otherDealOutcome,
      companyName,
      companyLogo,
    },
    isOwner,
    setDealRoom,
    openDealStatusDrawer,
    openTransferDrawer,
  } = useContext(DealContext);
  const { isTransferringDeal } = useContext(ListingContext);

  const { isSuperAdmin, hasEnterprise } = useContext(CurrentUserContext);

  const canTransferDeal = useMemo(
    () => isOwner && hasEnterprise,
    [hasEnterprise, isOwner],
  );

  const [editDrawer, editDrawerRef] = useCallbackRef<SideDrawerRef>();
  const [
    transferDealToEnterpriseColleagueModal,
    transferDealToEnterpriseColleagueModalRef,
  ] = useCallbackRef<ModalRef>();

  const classes = classNames('deal-details-card', className);

  const navigate = useNavigate();

  const updateHasPremium = useCallback(
    (hasBossModeUnlockedPremiumFeatures: boolean) =>
      setDealRoom((prev) => ({
        ...prev,
        hasBossModeUnlockedPremiumFeatures,
      })),
    [setDealRoom],
  );

  const {
    canLockPremiumFeatures,
    canUnlockPremiumFeatures,
    updateHasPremiumFeatures,
  } = usePremiumFeatures(dealRoom, updateHasPremium);

  const options = useMemo(
    () => [
      {
        label: t('editDealOverview'),
        icon: <PencilIcon />,
        disabled: isDisabled,
        onClick: () => {
          if (isDisabled) return;
          navigate(getCreateDealRoute(dealRoomId, true));
        },
      },
      {
        label: t('changeDealStatus'),
        icon: <TagIcon />,
        disabled: isDisabled,
        onClick: () => {
          if (isDisabled) return;
          openDealStatusDrawer();
        },
      },
      ...(canTransferDeal
        ? [
            {
              label: t('transferDeal'),
              icon: <TransferArrowsIcon />,
              disabled: isDisabled || isTransferringDeal,
              onClick: () => {
                if (isDisabled || isTransferringDeal) return;
                transferDealToEnterpriseColleagueModal?.open();
              },
            },
          ]
        : []),
    ],
    [
      t,
      isDisabled,
      canTransferDeal,
      isTransferringDeal,
      navigate,
      dealRoomId,
      openDealStatusDrawer,
      transferDealToEnterpriseColleagueModal,
    ],
  );

  const superAdminOptions = useMemo(
    () => [
      {
        label: t('resolveInternalDetails'),
        icon: <PercentageIcon />,
        onClick: () => editDrawer?.setOpen(true),
      },
      {
        label: t('transferOwnership'),
        icon: <TransferArrowsIcon />,
        onClick: () => openTransferDrawer(),
      },
      ...(canUnlockPremiumFeatures
        ? [
            {
              label: t('unlockAllFeatures'),
              icon: <LockopenIcon />,
              onClick: () => updateHasPremiumFeatures(true),
            },
          ]
        : []),
      ...(canLockPremiumFeatures
        ? [
            {
              label: t('lockAllFeatures'),
              icon: <LockclosedIcon />,
              onClick: () => updateHasPremiumFeatures(false),
            },
          ]
        : []),
    ],
    [
      canLockPremiumFeatures,
      canUnlockPremiumFeatures,
      editDrawer,
      t,
      updateHasPremiumFeatures,
      openTransferDrawer,
    ],
  );

  const uploadCompanyLogo: DropzoneUpload = useCallback(
    async (file, _, config) => {
      const { uploadedFile, dealRoom } = await api.dealRoom.uploadDealFile(
        file,
        'companyLogo',
        dealRoomId,
        config,
      );
      setDealRoom((old) => ({ ...old, ...dealRoom }));
      return uploadedFile;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dealRoomId],
  );

  const deleteCompanyLogo: DropzoneDelete = useCallback(
    async (file, _) => {
      const {
        data: { fileId, dealRoom },
      } = await api.dealRoom.deleteDealFile('companyLogo', file.id, dealRoomId);
      setDealRoom((old) => ({ ...old, ...dealRoom }));
      return fileId;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dealRoomId],
  );

  return (
    <div className={classes}>
      <div className="card-heading card-heading--row">
        <p>{t('dealOverview')}</p>
        <span>
          {isSuperAdmin && (
            <ThreeDotsDropdown
              options={superAdminOptions}
              trigger={
                <Button icon={LockclosedIcon} variant="outline">
                  Resolve
                </Button>
              }
            />
          )}
          <ThreeDotsDropdown options={options} />
        </span>
      </div>

      <div className="deal-details-card__content-wrapper">
        <table className="deal-details-card__content">
          <tbody>
            <tr>
              <td>{t('nameOfThisDeal')}</td>
              <td>{name}</td>
            </tr>
            <tr>
              <td>{t('industry')}</td>
              <td>{industry || t('na')}</td>
            </tr>
            <tr>
              <td>{t('dealOutcome')}</td>
              <td>{t(dealOutcome || 'na')}</td>
            </tr>
            {dealOutcome === 'other' && (
              <tr>
                <td>{t('pleaseSpecifyOther')}</td>
                <td>{t(otherDealOutcome)}</td>
              </tr>
            )}
            <tr>
              <td>{t('yourCompanyName')}</td>
              <td>{companyName || t('na')}</td>
            </tr>

            <tr>
              <td>{t('yourCompanyLogo')}</td>
              <td>
                <Dropzone
                  className="company-logo"
                  accept="image/*"
                  name="companyLogo"
                  maxFiles={1}
                  disabled={isDisabled}
                  fileName={companyLogo?.name}
                  files={companyLogo ? [companyLogo] : []}
                  uploadRequest={uploadCompanyLogo}
                  deleteRequest={deleteCompanyLogo}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {isSuperAdmin && <DealEditDrawer ref={editDrawerRef} />}

      {canTransferDeal && (
        <TransferDealToEnterpriseColleagueModal
          ref={transferDealToEnterpriseColleagueModalRef}
          dealRoomId={dealRoomId}
        />
      )}
    </div>
  );
};

export default DealDetailsFormCard;
